import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { can, useMe } from '../../auth.tsx';
import { activeItemTo, itemsOf, visibleAreas } from '../../navigation.ts';

/**
 * Configuração: um submenu próprio ao lado das telas (Visão geral, Plataforma, Usuários, Grupos de
 * usuários, Histórico), só com o que a pessoa pode abrir. No celular, o submenu vira uma faixa
 * que rola na horizontal acima da tela.
 */
export function SettingsLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const area = useMemo(
    () => visibleAreas((p) => can(me.data, p)).find((a) => a.id === 'settings'),
    [me.data],
  );
  const items = area ? itemsOf(area) : [];
  const active = area ? activeItemTo([area], pathname) : undefined;
  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-start">
      <nav
        aria-label={t('settings.menu')}
        className="-mx-4 overflow-x-auto px-4 md:sticky md:top-0 md:mx-0 md:w-52 md:shrink-0 md:overflow-visible md:px-0"
      >
        <p className="mb-2 hidden px-2.5 text-xs font-semibold text-muted md:block">
          {t('areas.settings')}
        </p>
        <ul className="flex gap-1 md:flex-col md:gap-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            const on = item.to === active;
            return (
              <li key={item.to} className="shrink-0">
                <Link
                  to={item.to}
                  aria-current={on ? 'page' : undefined}
                  className={`flex min-h-9 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap ${
                    on ? 'bg-brand-soft font-medium text-brand-text' : 'text-ink hover:bg-sheet'
                  }`}
                >
                  <Icon
                    size={16}
                    strokeWidth={1.75}
                    aria-hidden
                    className={`shrink-0 ${on ? 'text-brand-text' : 'text-muted'}`}
                  />
                  {t(item.key)}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="flex min-w-0 flex-1 flex-col gap-6 *:shrink-0">
        <Outlet />
      </div>
    </div>
  );
}
